import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileText, Printer, Download, FileDown } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const brl = (n) => (Number(n || 0)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const ALL = "__all__";

function toCSV(report) {
  const header = ["Data", "Solicitante", "Item", "Descrição", "Quantidade", "Unidade", "Valor Unit.", "Valor Total", "Status", "Observação"];
  const rows = report.pedidos.map(p => [
    new Date(p.created_at).toLocaleString("pt-BR"),
    p.solicitante_nome, p.item_nome, p.descricao,
    p.quantidade, p.unidade,
    (p.valor_unitario ?? 0).toFixed(2).replace(".", ","),
    (p.valor_total ?? 0).toFixed(2).replace(".", ","),
    p.status || "pendente",
    (p.observacao || "").replace(/\n/g, " ")
  ]);
  return [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
}

export default function RelatorioTab() {
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [start, setStart] = useState(monthAgo);
  const [end, setEnd] = useState(today);
  const [solicitanteId, setSolicitanteId] = useState(ALL);
  const [solicitantes, setSolicitantes] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/solicitantes").then(({ data }) => setSolicitantes(data)).catch(() => {});
  }, []);

  const gerar = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ start, end });
      if (solicitanteId && solicitanteId !== ALL) params.append("solicitante_id", solicitanteId);
      const { data } = await api.get(`/relatorio?${params.toString()}`);
      setReport(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const exportCSV = () => {
    if (!report) return;
    const csv = toCSV(report);
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio_${start}_a_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!report) return;
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Relatório de Pedidos", 40, 40);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    const fmtDate = (d) => new Date(d + "T00:00:00").toLocaleDateString("pt-BR");
    doc.text(`Período: ${fmtDate(start)} a ${fmtDate(end)}`, 40, 58);
    doc.text(`Solicitante: ${solicitanteLabel()}`, 40, 72);
    doc.text(`Total de pedidos: ${report.total_pedidos}`, 40, 86);
    doc.text(`Valor total geral: ${brl(report.valor_total_geral)}`, 40, 100);
    doc.setFontSize(8); doc.setTextColor(120);
    doc.text(`Gerado em ${new Date().toLocaleString("pt-BR")}`, pageWidth - 40, 40, { align: "right" });
    doc.setTextColor(0);

    // Totais por item
    autoTable(doc, {
      startY: 120,
      head: [["Item", "Nº pedidos", "Qtd total", "Valor unit.", "Valor total"]],
      body: report.totais_por_item.map(t => [
        t.item_nome, String(t.num_pedidos),
        `${t.quantidade_total} ${t.unidade}`,
        brl(t.valor_unitario), brl(t.valor_total),
      ]),
      foot: [[
        { content: "Total geral", colSpan: 4, styles: { halign: "right", fontStyle: "bold" } },
        { content: brl(report.valor_total_geral), styles: { fontStyle: "bold" } },
      ]],
      styles: { fontSize: 9, cellPadding: 5 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255 },
      footStyles: { fillColor: [241, 245, 249], textColor: 15, fontStyle: "bold" },
      columnStyles: {
        1: { halign: "right" }, 2: { halign: "right" },
        3: { halign: "right" }, 4: { halign: "right" },
      },
      margin: { left: 40, right: 40 },
      didDrawPage: (data) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("Totais por item", 40, data.settings.startY - 6);
      },
    });

    // Detalhados
    const startY2 = (doc.lastAutoTable?.finalY || 200) + 24;
    autoTable(doc, {
      startY: startY2 + 14,
      head: [["Data", "Solicitante", "Item", "Descrição", "Qtd", "Valor unit.", "Valor total", "Status"]],
      body: report.pedidos.map(p => [
        new Date(p.created_at).toLocaleString("pt-BR"),
        p.solicitante_nome, p.item_nome, p.descricao || "-",
        `${p.quantidade} ${p.unidade}`,
        brl(p.valor_unitario), brl(p.valor_total),
        (p.status || "pendente"),
      ]),
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255 },
      columnStyles: {
        4: { halign: "right" }, 5: { halign: "right" }, 6: { halign: "right" },
      },
      margin: { left: 40, right: 40 },
      didDrawPage: (data) => {
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.text("Solicitações detalhadas", 40, data.settings.startY - 6);
      },
    });

    doc.save(`relatorio_${start}_a_${end}.pdf`);
  };

  const solicitanteLabel = () => {
    if (!solicitanteId || solicitanteId === ALL) return "Todos os solicitantes";
    const s = solicitantes.find(x => x.id === solicitanteId);
    return s ? s.nome : "Solicitante";
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Relatório por período</CardTitle>
          <CardDescription>Escolha datas, opcionalmente filtre por solicitante e gere o relatório com valores.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Data inicial</Label>
              <Input data-testid="rel-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Data final</Label>
              <Input data-testid="rel-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
            </div>
            <div className="space-y-2 min-w-[220px]">
              <Label>Solicitante</Label>
              <Select value={solicitanteId} onValueChange={setSolicitanteId}>
                <SelectTrigger data-testid="rel-solicitante"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Todos os solicitantes</SelectItem>
                  {solicitantes.map(s => (<SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <Button data-testid="rel-gerar" onClick={gerar} disabled={loading} className="btn-brand rounded-full h-11">
              <FileText className="h-4 w-4 mr-2" /> {loading ? "Gerando..." : "Gerar relatório"}
            </Button>
            {report && (
              <>
                <Button variant="outline" onClick={exportPDF} data-testid="rel-pdf" className="rounded-full h-11">
                  <FileDown className="h-4 w-4 mr-2" /> Exportar PDF
                </Button>
                <Button variant="outline" onClick={exportCSV} data-testid="rel-csv" className="rounded-full h-11">
                  <Download className="h-4 w-4 mr-2" /> Exportar CSV
                </Button>
                <Button variant="outline" onClick={() => window.print()} data-testid="rel-imprimir" className="rounded-full h-11">
                  <Printer className="h-4 w-4 mr-2" /> Imprimir
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {report && (
        <>
          <div className="grid md:grid-cols-4 gap-4">
            <Card><CardContent className="p-6">
              <div className="text-xs uppercase text-slate-500 tracking-wide">Total de pedidos</div>
              <div className="text-3xl font-heading font-bold mt-2" data-testid="rel-total-pedidos">{report.total_pedidos}</div>
            </CardContent></Card>
            <Card><CardContent className="p-6">
              <div className="text-xs uppercase text-slate-500 tracking-wide">Itens distintos</div>
              <div className="text-3xl font-heading font-bold mt-2">{report.totais_por_item.length}</div>
            </CardContent></Card>
            <Card><CardContent className="p-6">
              <div className="text-xs uppercase text-slate-500 tracking-wide">Valor total</div>
              <div className="text-3xl font-heading font-bold mt-2 text-brand" data-testid="rel-valor-total">{brl(report.valor_total_geral)}</div>
            </CardContent></Card>
            <Card><CardContent className="p-6">
              <div className="text-xs uppercase text-slate-500 tracking-wide">Período / Solicitante</div>
              <div className="text-sm font-medium mt-2">{start} a {end}</div>
              <div className="text-sm text-slate-500 mt-1">{solicitanteLabel()}</div>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">Totais por item</CardTitle></CardHeader>
            <CardContent>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Nº pedidos</TableHead>
                    <TableHead className="text-right">Quantidade total</TableHead>
                    <TableHead className="text-right">Valor unit.</TableHead>
                    <TableHead className="text-right">Valor total</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {report.totais_por_item.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-6">—</TableCell></TableRow>}
                    {report.totais_por_item.map(t => (
                      <TableRow key={t.item_id}>
                        <TableCell className="font-medium">{t.item_nome}</TableCell>
                        <TableCell className="text-right">{t.num_pedidos}</TableCell>
                        <TableCell className="text-right font-mono">{t.quantidade_total} {t.unidade}</TableCell>
                        <TableCell className="text-right font-mono">{brl(t.valor_unitario)}</TableCell>
                        <TableCell className="text-right font-mono font-semibold">{brl(t.valor_total)}</TableCell>
                      </TableRow>
                    ))}
                    {report.totais_por_item.length > 0 && (
                      <TableRow className="bg-slate-50">
                        <TableCell colSpan={4} className="text-right font-heading font-bold">Total geral</TableCell>
                        <TableCell className="text-right font-mono font-bold text-brand">{brl(report.valor_total_geral)}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="font-heading text-lg">Solicitações detalhadas</CardTitle></CardHeader>
            <CardContent>
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Data</TableHead><TableHead>Solicitante</TableHead>
                    <TableHead>Item</TableHead><TableHead>Descrição</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    <TableHead className="text-right">Valor unit.</TableHead>
                    <TableHead className="text-right">Valor total</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {report.pedidos.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-6">Sem pedidos no período.</TableCell></TableRow>}
                    {report.pedidos.map(p => {
                      const st = p.status || "pendente";
                      const cls = st === "atendido" ? "text-emerald-700" : st === "cancelado" ? "text-slate-500" : "text-amber-700";
                      const label = st === "atendido" ? "Atendido" : st === "cancelado" ? "Cancelado" : "Pendente";
                      return (
                        <TableRow key={p.id}>
                          <TableCell className="text-sm text-slate-600">{new Date(p.created_at).toLocaleString("pt-BR")}</TableCell>
                          <TableCell className="font-medium">{p.solicitante_nome}</TableCell>
                          <TableCell>{p.item_nome}</TableCell>
                          <TableCell className="text-sm text-slate-600">{p.descricao}</TableCell>
                          <TableCell className="text-right font-mono">{p.quantidade} {p.unidade}</TableCell>
                          <TableCell className="text-right font-mono">{brl(p.valor_unitario)}</TableCell>
                          <TableCell className="text-right font-mono font-semibold">{brl(p.valor_total)}</TableCell>
                          <TableCell className={`text-sm font-medium ${cls}`}>{label}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
